import React, { useEffect, useRef, useState, useContext } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { db } from '../firebase';
import { CartContext } from '../contexts/CartContext';
import { AuthContext } from '../contexts/AuthContext';
import { StoreContext } from '../contexts/StoreContext';
import { CardBrandLabel } from '../components/CardBrandIcon';
import {
  MAX_CONTENT_WIDTH,
  BORDER_RADIUS,
  SPACING_TIGHT,
  SPACING_ELEMENT,
  SPACING_COMPACT,
  SPACING_COMPONENT,
  SPACING_BUTTON,
  SPACING_CARD_BODY,
  SPACING_GROUP,
  SPACING_HERO,
} from '../constants/layout';
import {
  FONT_BODY,
  FONT_SUBHEADLINE,
  FONT_FOOTNOTE,
  FONT_CAPTION,
} from '../constants/typography';
import { formatPrice } from '../utils/priceFormatter';
import { formatAddress } from '../utils/address';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import { PAYMENT_STATUS } from '../constants/paymentStatus';
import { getFulfillmentStatusLabel } from '../constants/fulfillmentStatus';
import stripePromise from '../contexts/StripeContext';
import {
  clearSubmittedQuote,
  consumeSubmittedQuote,
  parseStoredCheckoutQuoteV2Document,
  paymentIntentReturnDisposition,
  submitFreeOrderV2,
  validateMaterializedOrderV2,
} from '../services/paymentV2';
import { normalizeOrderAmounts } from '../utils/orderAmounts';

function logCheckoutError({ sessionId, userID, step, errorMessage, errorCode }) {
  addDoc(collection(db, 'checkout_errors'), {
    timestamp: serverTimestamp(),
    sessionId: sessionId || '',
    userID: userID || '',
    platform: 'web',
    step,
    errorMessage: errorMessage || '',
    ...(errorCode != null ? { errorCode } : {}),
  }).catch(() => {});
}

export default function OrderConfirmationPage() {
  const [searchParams] = useSearchParams();
  const { orderId: paramOrderId } = useParams();
  const navigate = useNavigate();
  const { removePurchasedItems } = useContext(CartContext);
  const { user, loading: authLoading } = useContext(AuthContext);
  const { store } = useContext(StoreContext);
  const { showPrices } = useDisplayPreferences();
  const removePurchasedItemsRef = useRef(removePurchasedItems);

  const queryOrderId = searchParams.get('orderId');
  const queryQuoteId = searchParams.get('quoteId');
  const querySessionId = searchParams.get('sessionId');
  const returnedPaymentIntentId = searchParams.get('payment_intent');
  const returnedClientSecret = searchParams.get('payment_intent_client_secret');
  const orderId = paramOrderId || queryOrderId;
  const isPostCheckout = Boolean(queryQuoteId || querySessionId);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pending, setPending] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [paymentRestartRequired, setPaymentRestartRequired] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    removePurchasedItemsRef.current = removePurchasedItems;
  }, [removePurchasedItems]);

  useEffect(() => {
    setLoading(true);
    setError(false);
    setPending(false);
    setPaymentFailed(false);
    setPaymentRestartRequired(false);
    setOrder(null);

    if (!queryQuoteId && !querySessionId && !orderId) {
      setError(true);
      setLoading(false);
      return;
    }

    if (authLoading) return;

    function extractOrder(data, docId) {
      const amounts = normalizeOrderAmounts(data);
      return {
        id: docId,
        orderNumber: data.orderNumber,
        timestamp: data.timestamp?.toDate?.() || null,
        items: data.items || [],
        currency: data.currency || 'USD',
        subtotalAmount: amounts.subtotalAmount,
        taxAmount: amounts.taxAmount,
        totalAmount: amounts.totalAmount,
        paymentCollection: data.paymentCollection || null,
        paymentStatus: data.paymentStatus || null,
        refundedAmount: data.refundedAmount ?? null,
        cardBrand: data.cardBrand || null,
        cardLast4: data.cardLast4 || null,
        userName: data.userName || '',
        userEmail: data.userEmail || '',
        userPhone: data.userPhone || '',
        pickupName: data.pickupName || '',
        pickupPhone: data.pickupPhone || '',
        customInstructions: data.customInstructions || '',
        statuses: data.statuses || [],
      };
    }

    let cancelled = false;

    async function pollForOrder(loadOrder, correlationId) {
      const MAX_ATTEMPTS = 10;
      const POLL_INTERVAL = 1500;

      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        if (cancelled) return null;
        const foundOrder = await loadOrder();
        if (cancelled) return null;
        if (foundOrder) return foundOrder;
        if (attempt < MAX_ATTEMPTS - 1) {
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));
        }
      }

      if (cancelled) return null;
      logCheckoutError({
        sessionId: correlationId,
        userID: user?.uid,
        step: 'order_confirmation_pending',
        errorMessage: 'Order creation was still pending after confirmation polling completed.',
      });
      return null;
    }

    async function fetchOrder() {
      try {
        if (queryQuoteId && user) {
          // V2 orders use the immutable quote ID as their deterministic document ID.
          // Verify the Stripe return when its client secret is present. Only
          // terminally failed/canceled intents permit another checkout attempt.
          let returnDisposition = 'pending';
          const quoteSnap = await getDoc(
            doc(db, 'checkout_quotes_v2', queryQuoteId),
          );
          if (!quoteSnap.exists()) {
            throw new Error('The payment quote could not be verified.');
          }
          const verifiedQuote = parseStoredCheckoutQuoteV2Document(
            quoteSnap.data(),
            queryQuoteId,
            user.uid,
          );
          let freeSubmissionError = null;
          if (verifiedQuote.paymentCollection === 'not_required') {
            if (returnedClientSecret || returnedPaymentIntentId) {
              throw new Error(
                'A payment-free order returned unexpected Stripe identifiers.',
              );
            }
            try {
              const functions = getFunctions(getApp(), 'us-central1');
              await submitFreeOrderV2(functions, verifiedQuote);
            } catch (submissionError) {
              freeSubmissionError = submissionError;
              logCheckoutError({
                sessionId: queryQuoteId,
                userID: user.uid,
                step: 'free_order_v2_reconciliation',
                errorMessage:
                  submissionError?.message ||
                  'The payment-free order could not be reconciled.',
                errorCode: submissionError?.code,
              });
            }
          } else if (returnedClientSecret && stripePromise) {
            try {
              const stripe = await stripePromise;
              const result = await stripe.retrievePaymentIntent(returnedClientSecret);
              if (result.error || !result.paymentIntent) {
                throw result.error || new Error('Stripe did not return a PaymentIntent.');
              }
              if (verifiedQuote.paymentIntentId !== result.paymentIntent.id) {
                throw new Error('The returned PaymentIntent is not bound to this quote.');
              }
              if (
                returnedPaymentIntentId &&
                result.paymentIntent.id !== returnedPaymentIntentId
              ) {
                throw new Error('The returned PaymentIntent identifier does not match.');
              }
              if (
                result.paymentIntent.amount !== verifiedQuote.totalMinor ||
                String(result.paymentIntent.currency || '').toUpperCase() !==
                  verifiedQuote.currency
              ) {
                throw new Error('The returned PaymentIntent amount or currency changed.');
              }
              returnDisposition = paymentIntentReturnDisposition(
                result.paymentIntent.status,
              );
            } catch (returnError) {
              logCheckoutError({
                sessionId: queryQuoteId,
                userID: user.uid,
                step: 'payment_return_verification',
                errorMessage: returnError?.message || 'Could not verify the Stripe return.',
                errorCode: returnError?.code,
              });
            }
          }

          if (returnDisposition === 'retryable') {
            clearSubmittedQuote(queryQuoteId);
            setPaymentFailed(true);
            return;
          }
          if (returnDisposition === 'restart') {
            consumeSubmittedQuote(queryQuoteId);
            setPaymentRestartRequired(true);
            setPaymentFailed(true);
            return;
          }
          const foundOrder = await pollForOrder(async () => {
            const snap = await getDoc(doc(db, 'orders', queryQuoteId));
            if (!snap.exists()) return null;
            const data = snap.data();
            if (data.userID !== user.uid) {
              throw new Error('The confirmed order is not owned by the authenticated user.');
            }
            // A processing PaymentIntent can materialize a pending order before
            // its financial outcome is known. Keep polling and preserve the
            // cart until the server reaches a terminal customer-facing state.
            if (!data.paymentStatus || data.paymentStatus === 'pending') {
              return null;
            }
            validateMaterializedOrderV2(data, snap.id, verifiedQuote);
            return extractOrder(data, snap.id);
          }, queryQuoteId);
          if (cancelled) return;
          if (foundOrder) {
            if (foundOrder.paymentStatus === 'failed') {
              // A failed confirmation normally returns the reusable Intent to
              // requires_payment_method. Preserve the durable attempt; the
              // resume path retrieves Stripe's current status and discards it
              // only if Stripe says it was actually canceled.
              clearSubmittedQuote(queryQuoteId);
              setPaymentFailed(true);
            } else {
              // A client-side submission marker is only a UX hint. Keep the
              // cart until the server has materialized a non-pending,
              // quote-bound order; delayed or failed processing must remain
              // recoverable from the original cart. The cart subtraction and
              // its quote-scoped replay ledger commit atomically in IndexedDB.
              // Do not clear the submitted marker until that commit succeeds.
              await removePurchasedItemsRef.current(foundOrder.items, {
                referenceType: 'quote',
                referenceId: queryQuoteId,
              });
              if (cancelled) return;
              setOrder(foundOrder);
              consumeSubmittedQuote(queryQuoteId);
            }
          } else if (
            freeSubmissionError &&
            [
              'functions/invalid-argument',
              'functions/failed-precondition',
              'functions/not-found',
              'functions/permission-denied',
              'functions/unauthenticated',
            ].includes(freeSubmissionError?.code)
          ) {
            consumeSubmittedQuote(queryQuoteId);
            setPaymentRestartRequired(true);
            setPaymentFailed(true);
          } else {
            setPending(true);
          }
        } else if (querySessionId && user) {
          // Legacy links remain readable for already-created checkouts.
          const foundOrder = await pollForOrder(async () => {
            const q = query(
              collection(db, 'orders'),
              where('checkoutSessionId', '==', querySessionId),
              where('userID', '==', user.uid)
            );
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
              return extractOrder(snapshot.docs[0].data(), snapshot.docs[0].id);
            }
            return null;
          }, querySessionId);
          if (cancelled) return;
          if (foundOrder) {
            // Legacy links have no pre-payment IndexedDB proof, so the
            // repository preserves the cart. Passing the immutable order ID
            // keeps this path compatible if legacy proof is ever imported.
            await removePurchasedItemsRef.current(foundOrder.items, {
              referenceType: 'order',
              referenceId: foundOrder.id,
            });
            if (cancelled) return;
            setOrder(foundOrder);
          } else {
            setPending(true);
          }
        } else if (orderId) {
          // Direct navigation: fetch by document ID
          const snap = await getDoc(doc(db, 'orders', orderId));
          if (snap.exists()) {
            const data = snap.data();
            if (user && data.userID !== user.uid) {
              throw new Error('The requested order is not owned by the authenticated user.');
            }
            setOrder(extractOrder(data, snap.id));
          } else {
            setError(true);
          }
        } else {
          setError(true);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch order:', err);
        logCheckoutError({
          sessionId: queryQuoteId || querySessionId || orderId,
          userID: user?.uid,
          step: 'order_fetch_error',
          errorMessage: err.message || 'Failed to fetch order',
        });
        setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchOrder();
    return () => {
      cancelled = true;
    };
  }, [
    queryQuoteId,
    querySessionId,
    orderId,
    returnedPaymentIntentId,
    returnedClientSecret,
    user,
    authLoading,
  ]);

  if (loading) {
    return (
      <div style={{ maxWidth: MAX_CONTENT_WIDTH, margin: '48px auto', textAlign: 'center' }}>
        <h2>{isPostCheckout ? 'Confirming your order...' : 'Loading order...'}</h2>
      </div>
    );
  }

  if (pending) {
    return (
      <div style={{ maxWidth: MAX_CONTENT_WIDTH, margin: '48px auto', textAlign: 'center' }}>
        <h2>Your order is being confirmed</h2>
        <p style={{ color: 'var(--color-text-secondary)' }}>
          Your checkout was submitted, but the order is still being finalized. Do not place it again. It will appear in your order history shortly.
        </p>
        {queryQuoteId && (
          <button
            onClick={() => navigate(`/checkout?resumeQuoteId=${encodeURIComponent(queryQuoteId)}`)}
            style={{ marginTop: 12, marginRight: 8, padding: '8px 20px', cursor: 'pointer' }}
          >
            Resume this checkout
          </button>
        )}
        <button
          onClick={() => navigate('/orderhistory')}
          style={{ marginTop: 12, padding: '8px 20px', cursor: 'pointer' }}
        >
          View Orders
        </button>
      </div>
    );
  }

  if (paymentFailed) {
    return (
      <div style={{ maxWidth: MAX_CONTENT_WIDTH, margin: '48px auto', textAlign: 'center' }}>
        <h2>Checkout wasn&apos;t completed</h2>
        <p style={{ color: 'var(--color-text-secondary)' }}>
          {paymentRestartRequired
            ? 'This checkout can no longer be completed. Your cart is still available.'
            : 'This payment needs a new payment method. Your cart is still available and the same secure payment will be reused.'}
        </p>
        <button
          onClick={() => navigate(
            !paymentRestartRequired && queryQuoteId
              ? `/checkout?resumeQuoteId=${encodeURIComponent(queryQuoteId)}`
              : '/cart',
          )}
          style={{ marginTop: 12, padding: '8px 20px', cursor: 'pointer' }}
        >
          {paymentRestartRequired ? 'Return to Cart' : 'Retry Secure Payment'}
        </button>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div style={{ maxWidth: MAX_CONTENT_WIDTH, margin: '48px auto', textAlign: 'center' }}>
        <h2>Order Status</h2>
        <p style={{ color: 'var(--color-text-secondary)' }}>
          We couldn't load your order details. Your order may still have been placed — please check your order history.
        </p>
        <button
          onClick={() => navigate('/orderhistory')}
          style={{ marginTop: 12, padding: '8px 20px', cursor: 'pointer' }}
        >
          View Orders
        </button>
      </div>
    );
  }

  const totalItems = order.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const calculatedItemsSubtotal = order.items.reduce((subtotal, item) => {
    const optionAmount = (item.selectedOptions || []).reduce(
      (sum, option) => sum + (option.unitAmount || 0),
      0,
    );
    return subtotal + ((item.unitAmount || 0) + optionAmount) * (item.quantity || 0);
  }, 0);
  const displaySubtotal = order.subtotalAmount ?? calculatedItemsSubtotal;
  const displayTax = order.taxAmount ?? 0;
  const orderTotal = order.totalAmount ?? (displaySubtotal + displayTax);

  const formatDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    }).format(date);
  };

  const handleExportPdf = async () => {
    if (!order?.id) return;
    setIsExporting(true);
    try {
      const functions = getFunctions(getApp(), 'us-central1');
      const exportOrderPdf = httpsCallable(functions, 'exportOrderPdf');
      const result = await exportOrderPdf({ orderId: order.id });
      const base64 = result.data?.pdf;
      if (base64) {
        const byteChars = atob(base64);
        const byteArray = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteArray[i] = byteChars.charCodeAt(i);
        }
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Order-${String(order.orderNumber).padStart(3, '0')}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('PDF export error:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const paymentStatusInfo = order.paymentStatus && order.paymentStatus !== 'paid'
    ? PAYMENT_STATUS[order.paymentStatus] || null
    : null;
  const isAuthorized = order.paymentStatus === 'authorized';
  const isPaymentFree = order.paymentCollection === 'not_required' &&
    order.paymentStatus === 'notRequired';
  const showPaymentStatusBadge = isAuthorized || isPaymentFree;
  const refundStatusInfo = paymentStatusInfo && !showPaymentStatusBadge
    ? paymentStatusInfo
    : null;

  return (
    <div className="order-detail-container">
      {/* Header */}
      <div className="detail-section">
        <div className="section-card">
          <div className="order-header-content">
            <div className="order-title-line">
              <span className="order-title">
                Order #{String(order.orderNumber).padStart(3, '0')}
              </span>
              {showPaymentStatusBadge && paymentStatusInfo && (
                <span
                  className="payment-status-badge"
                  style={{
                    color: paymentStatusInfo.color,
                    backgroundColor: `${paymentStatusInfo.color}1F`,
                  }}
                >
                  {paymentStatusInfo.label}
                </span>
              )}
            </div>
            {order.timestamp && (
              <div className="order-meta">{formatDate(order.timestamp)}</div>
            )}
            <div className="order-meta">
              {totalItems} {totalItems === 1 ? 'item' : 'items'}
            </div>
          </div>
        </div>
      </div>

      {/* Status */}
      {order.statuses.length > 0 && (
        <div className="detail-section">
          <div className="section-label">Status</div>
          <div className="section-card">
            {[...order.statuses]
              .sort((a, b) => {
                const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date?.seconds ? a.date.seconds * 1000 : a.date);
                const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date?.seconds ? b.date.seconds * 1000 : b.date);
                return dateB - dateA;
              })
              .map((s, i) => {
                const statusDate = s.date?.toDate ? s.date.toDate() : new Date(s.date?.seconds ? s.date.seconds * 1000 : s.date);
                return (
                  <div key={s.id || i} className={`status-row${i === 0 ? ' status-current' : ''}`}>
                    <span className="status-label">{getFulfillmentStatusLabel(s.status)}</span>
                    <span className="status-date">{formatDate(statusDate)}</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Your Order */}
      <div className="detail-section">
        <div className="section-label">Your Order</div>
        <div className="section-card">
          {order.items.map((item, idx) => {
            const optionsByGroup = {};
            (item.selectedOptions || []).forEach(opt => {
              if (!optionsByGroup[opt.id]) {
                optionsByGroup[opt.id] = { title: opt.title, count: 0 };
              }
              optionsByGroup[opt.id].count += 1;
            });

            return (
              <div key={idx} className={`line-item${idx < order.items.length - 1 ? ' line-item-bordered' : ''}`}>
                <div className="line-item-row">
                  <div className="line-item-left">
                    <span className="line-item-qty">{item.quantity}x</span>
                    <span className="line-item-name">{item.productName}</span>
                  </div>
                  {showPrices && (
                    <span className="line-item-price">
                      {formatPrice(
                        (item.unitAmount + (item.selectedOptions || []).reduce((s, o) => s + (o.unitAmount || 0), 0)) * item.quantity,
                        order.currency
                      )}
                    </span>
                  )}
                </div>
                {Object.keys(optionsByGroup).length > 0 && (
                  <div className="line-item-options">
                    {Object.values(optionsByGroup).map((opt, oi) => (
                      <div key={oi} className="option-row">
                        <span className="option-title">{opt.title}</span>
                        <span className="option-count">x{opt.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      {showPrices && (
        <div className="detail-section">
          <div className="section-label">Summary</div>
          <div className="section-card">
            <div className="summary-row">
              <span className="summary-label-text">Subtotal</span>
              <span className="summary-value-text">
                {order.subtotalAmount != null
                  ? formatPrice(order.subtotalAmount, order.currency)
                  : formatPrice(calculatedItemsSubtotal, order.currency)}
              </span>
            </div>
            <div className="summary-row">
              <span className="summary-label-text">Tax</span>
              <span className="summary-value-text">
                {formatPrice(order.taxAmount ?? 0, order.currency)}
              </span>
            </div>
            <div className="summary-row total">
              <span className="summary-total-label">Total</span>
              <span className="summary-total-value">
                {formatPrice(orderTotal, order.currency)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Refund Status */}
      {refundStatusInfo && (
        <div className="detail-section">
          <div className="section-card">
            <div className="refund-row">
              <span
                className="material-icons refund-icon"
                style={{ color: refundStatusInfo.color }}
              >
                {refundStatusInfo.icon}
              </span>
              <div className="refund-info">
                <div className="refund-status" style={{ color: refundStatusInfo.color }}>
                  {refundStatusInfo.label}
                </div>
                {order.refundedAmount != null && order.refundedAmount > 0 && (
                  <div className="refund-detail">
                    Amount Refunded: {formatPrice(order.refundedAmount, order.currency)}
                  </div>
                )}
                {order.paymentStatus === 'partiallyRefunded' &&
                  order.subtotalAmount != null && order.taxAmount != null && (
                  <div className="refund-detail">
                    Net Charged: {formatPrice(orderTotal - (order.refundedAmount || 0), order.currency)}
                  </div>
                )}
                {order.paymentStatus === 'disputed' && (
                  <div className="refund-detail">
                    This order is under review by your card issuer.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment */}
      {order.cardLast4 && (
        <div className="detail-section">
          <div className="section-label">Payment</div>
          <div className="section-card">
            <CardBrandLabel
              brand={order.cardBrand}
              last4={order.cardLast4}
              style={{ fontSize: FONT_SUBHEADLINE, color: 'var(--color-text-secondary)' }}
            />
          </div>
        </div>
      )}

      {/* Contact Info */}
      <div className="detail-section">
        <div className="section-label">Contact Info</div>
        <div className="section-card">
          <div className="info-row">
            <span className="info-label">Name</span>
            <span className="info-value">{order.userName || '-'}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Email</span>
            {order.userEmail ? (
              <a href={`mailto:${order.userEmail}`} className="info-value info-link">
                {order.userEmail}
              </a>
            ) : (
              <span className="info-value">-</span>
            )}
          </div>
          <div className="info-row">
            <span className="info-label">Phone</span>
            {order.userPhone ? (
              <a href={`tel:${order.userPhone}`} className="info-value info-link">
                {order.userPhone}
              </a>
            ) : (
              <span className="info-value">-</span>
            )}
          </div>
        </div>
      </div>

      {/* Pickup Person */}
      {order.pickupName && order.pickupName.trim() !== '' && (
        <div className="detail-section">
          <div className="section-label">Pickup Person</div>
          <div className="section-card">
            <div className="info-row">
              <span className="info-label">Name</span>
              <span className="info-value">{order.pickupName}</span>
            </div>
            {order.pickupPhone && order.pickupPhone.trim() !== '' && (
              <div className="info-row">
                <span className="info-label">Phone</span>
                <a href={`tel:${order.pickupPhone}`} className="info-value info-link">
                  {order.pickupPhone}
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pickup Details */}
      {store && (store.location || store.pickUpInstructions?.trim()) && (
        <div className="detail-section">
          <div className="section-label">Pickup Details</div>
          <div className="section-card">
            {store.location && (
              <div className="pickup-address">
                <span className="material-icons pickup-pin">location_on</span>
                <span className="pickup-address-text">{formatAddress(store.location)}</span>
              </div>
            )}
            {store.pickUpInstructions?.trim() && (
              <div className="pickup-instructions">{store.pickUpInstructions}</div>
            )}
          </div>
        </div>
      )}

      {/* Support Footer */}
      {store?.storeContact?.phoneNumber && (
        <div className="support-footer">
          <span>Questions about your order?</span>
          <a href={`tel:${store.storeContact.phoneNumber}`} className="support-link">
            Contact us at {store.storeContact.phoneNumber}
          </a>
        </div>
      )}

      <div style={{ marginTop: 20, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleExportPdf}
          disabled={isExporting}
          className="export-button"
        >
          {isExporting ? 'Exporting...' : 'Download Receipt'}
        </button>
        <button
          onClick={() => navigate('/orderhistory')}
          className="back-button"
        >
          {isPostCheckout ? 'View Orders' : 'Back to Orders'}
        </button>
      </div>

      <style jsx>{`
        .order-detail-container {
          max-width: 600px;
          margin: 0 auto;
          padding: ${SPACING_GROUP} ${SPACING_BUTTON} ${SPACING_HERO};
        }

        .detail-section {
          margin-bottom: ${SPACING_CARD_BODY};
        }

        .section-label {
          font-size: ${FONT_FOOTNOTE};
          font-weight: 600;
          color: var(--color-text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: ${SPACING_ELEMENT};
          padding-left: ${SPACING_TIGHT};
        }

        .section-card {
          background-color: var(--color-surface, var(--color-card-background, #fff));
          border: 1px solid var(--color-border);
          border-radius: ${SPACING_COMPACT};
          padding: ${SPACING_BUTTON};
        }

        .order-header-content {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .order-title-line {
          display: flex;
          align-items: center;
          gap: ${SPACING_ELEMENT};
        }

        .order-title {
          font-size: ${FONT_BODY};
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .payment-status-badge {
          font-size: ${FONT_CAPTION};
          font-weight: 600;
          padding: 2px 6px;
          border-radius: 999px;
        }

        .order-meta {
          font-size: ${FONT_CAPTION};
          color: var(--color-text-secondary);
        }

        .status-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 4px 0;
        }

        .status-current .status-label {
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .status-label {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-secondary);
        }

        .status-date {
          font-size: ${FONT_CAPTION};
          color: var(--color-text-secondary);
        }

        .line-item {
          padding: ${SPACING_ELEMENT} 0;
        }

        .line-item-bordered {
          border-bottom: 1px solid var(--color-border);
        }

        .line-item-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: ${SPACING_COMPONENT};
        }

        .line-item-left {
          display: flex;
          gap: ${SPACING_ELEMENT};
          align-items: baseline;
        }

        .line-item-qty {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-secondary);
        }

        .line-item-name {
          font-size: ${FONT_BODY};
          color: var(--color-text-primary);
        }

        .line-item-price {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-primary);
          flex-shrink: 0;
        }

        .line-item-options {
          padding-left: 28px;
          margin-top: 4px;
        }

        .option-row {
          display: flex;
          justify-content: space-between;
          font-size: ${FONT_CAPTION};
          color: var(--color-text-secondary);
          padding: 1px 0;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          padding: 4px 0;
        }

        .summary-label-text {
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
        }

        .summary-value-text {
          font-size: ${FONT_BODY};
          color: var(--color-text-primary);
        }

        .summary-row.total {
          margin-top: 4px;
        }

        .summary-total-label {
          font-size: ${FONT_BODY};
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .summary-total-value {
          font-size: ${FONT_BODY};
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .refund-row {
          display: flex;
          gap: ${SPACING_COMPONENT};
          align-items: flex-start;
        }

        .refund-icon {
          font-size: 22px;
          flex-shrink: 0;
        }

        .refund-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .refund-status {
          font-size: ${FONT_SUBHEADLINE};
          font-weight: 600;
        }

        .refund-detail {
          font-size: ${FONT_CAPTION};
          color: var(--color-text-secondary);
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          padding: 4px 0;
        }

        .info-label {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-primary);
        }

        .info-value {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-secondary);
          text-align: right;
        }

        .info-link {
          text-decoration: none;
          color: var(--color-text-secondary);
        }

        .pickup-address {
          display: flex;
          gap: ${SPACING_ELEMENT};
          align-items: flex-start;
        }

        .pickup-pin {
          color: #FF3B30;
          font-size: 20px;
          flex-shrink: 0;
        }

        .pickup-address-text {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-secondary);
        }

        .pickup-instructions {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-secondary);
          margin-top: ${SPACING_ELEMENT};
        }

        .support-footer {
          text-align: center;
          margin-top: ${SPACING_ELEMENT};
          display: flex;
          flex-direction: column;
          gap: ${SPACING_TIGHT};
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-secondary);
        }

        .support-link {
          color: var(--color-accent);
          text-decoration: none;
        }

        .export-button {
          padding: ${SPACING_COMPONENT} ${SPACING_GROUP};
          background-color: transparent;
          color: var(--color-accent, #000);
          border: 1px solid var(--color-accent, #000);
          border-radius: ${BORDER_RADIUS};
          font-size: ${FONT_BODY};
          font-weight: 600;
          cursor: pointer;
        }

        .export-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .back-button {
          padding: ${SPACING_COMPONENT} ${SPACING_GROUP};
          background-color: var(--color-accent, #000);
          color: var(--color-accent-text, #fff);
          border: none;
          border-radius: ${BORDER_RADIUS};
          font-size: ${FONT_BODY};
          font-weight: 600;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}
